import React from "react";
import { useTheme } from "hooks";
import "./LegalPage.styles.scss";

export default function EulaPage() {
  const { theme } = useTheme();
  const accentColor = (theme && theme.color1) || "#0077b6";

  return (
    <div className="legal-page" style={{ "--legal-accent": accentColor }}>
      {/* ── Header ── */}
      <header className="legal-header">
        <div className="legal-header-inner">
          <div className="legal-header-brand">
            {theme && theme.logo ? (
              <img src={theme.logo} alt="Citrus eCF" />
            ) : (
              <span>Citrus eCF</span>
            )}
          </div>
          <span className="legal-header-tag">Acuerdo de Licencia</span>
        </div>
      </header>

      {/* ── Content ── */}
      <div className="legal-body">
        <div className="legal-container">

          {/* Document header */}
          <div className="legal-doc-header">
            <span className="legal-doc-badge">EULA</span>
            <h1>End-User License Agreement</h1>
            <p className="legal-subtitle">
              Citrus eCF – Integración con QuickBooks Online
            </p>
            <div className="legal-meta">
              <span><strong>Fecha de Vigencia:</strong> 26 de febrero de 2026</span>
              <span><strong>Última Actualización:</strong> 26 de febrero de 2026</span>
            </div>
          </div>

          {/* Section 1 */}
          <div className="legal-section">
            <h2>1. Aceptación de los Términos</h2>
            <p>
              Al instalar, conectar o utilizar la integración de Citrus eCF para
              QuickBooks Online ("la Integración"), usted ("Usuario" o "usted")
              acepta quedar vinculado por este Acuerdo de Licencia de Usuario
              Final ("Acuerdo"). Si no está de acuerdo con estos términos, no
              conecte ni utilice la Integración.
            </p>
            <p>
              Este Acuerdo se celebra entre usted y <strong>Citrus Technology</strong>{" "}
              ("Citrus", "nosotros", "nos" o "nuestro"), el desarrollador de la
              plataforma Citrus eCF.
            </p>
          </div>

          {/* Section 2 */}
          <div className="legal-section">
            <h2>2. Descripción de la Integración</h2>
            <p>
              La integración Citrus eCF – QuickBooks conecta su cuenta de
              QuickBooks Online con la plataforma Citrus eCF para
              automatizar la generación y transmisión de{" "}
              <strong>Comprobantes Fiscales Electrónicos (eCF)</strong> ante la{" "}
              <strong>
                Dirección General de Impuestos Internos (DGII)
              </strong>{" "}
              de la República Dominicana.
            </p>
            <p>Específicamente, la Integración:</p>
            <ul>
              <li>
                Recupera <strong>Facturas</strong>,{" "}
                <strong>Órdenes de Compra</strong> y{" "}
                <strong>Notas de Crédito</strong> de su cuenta de QuickBooks
                Online.
              </li>
              <li>
                Recupera datos de referencia asociados, incluyendo{" "}
                <strong>Clientes</strong>, <strong>Suplidores</strong>,{" "}
                <strong>Artículos</strong>, <strong>Tasas de Impuesto</strong> y{" "}
                <strong>Códigos de Impuesto</strong>.
              </li>
              <li>
                Convierte esa información al formato de documento electrónico
                fiscal requerido por la DGII.
              </li>
              <li>
                Transmite los documentos eCF resultantes a la DGII para su
                validación y registro.
              </li>
              <li>
                Escribe en sus documentos de QuickBooks Online los datos fiscales resultantes:{" "}
                <strong>número de NCF</strong>, fecha de vencimiento del NCF,
                ID de transacción y tipo de comprobante, como campos
                personalizados.
              </li>
              <li>
                Adjunta las representaciones firmadas en <strong>XML</strong> y{" "}
                <strong>PDF</strong> de cada documento eCF al registro
                correspondiente en QuickBooks Online.
              </li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="legal-section">
            <h2>3. Concesión de Licencia</h2>
            <p>
              Sujeto a su cumplimiento de este Acuerdo, Citrus le otorga una
              licencia limitada, no exclusiva, intransferible y revocable para
              usar la Integración únicamente con fines comerciales internos,
              en conexión con su propia cuenta de QuickBooks Online y su propia
              cuenta de Citrus eCF.
            </p>
          </div>

          {/* Section 4 */}
          <div className="legal-section">
            <h2>4. Restricciones</h2>
            <p>Usted no podrá:</p>
            <ul>
              <li>
                Sublicenciar, vender, revender, transferir, ceder o explotar
                comercialmente de cualquier otra forma la Integración.
              </li>
              <li>
                Realizar ingeniería inversa, descompilar o desensamblar cualquier
                componente de la Integración.
              </li>
              <li>
                Utilizar la Integración para cualquier propósito que no sea el
                procesamiento de los documentos fiscales de su propia empresa en
                cumplimiento con la legislación tributaria dominicana.
              </li>
              <li>
                Intentar eludir cualquier mecanismo de seguridad de la
                Integración, QuickBooks Online o la DGII.
              </li>
              <li>
                Utilizar la Integración de manera que infrinja las leyes
                aplicables, incluidas las regulaciones de la DGII.
              </li>
            </ul>
          </div>

          {/* Section 5 */}
          <div className="legal-section">
            <h2>5. Cuenta de QuickBooks Online y Términos de Intuit</h2>
            <p>
              El uso de esta Integración requiere una cuenta válida de QuickBooks
              Online regida por los propios Términos de Servicio y Política de
              Privacidad de Intuit. Este Acuerdo no modifica ni reemplaza los
              términos de Intuit. Usted es el único responsable de mantener su
              cuenta de QuickBooks Online y de cumplir con los requisitos de
              Intuit.
            </p>
          </div>

          {/* Section 6 */}
          <div className="legal-section">
            <h2>6. Autorización y Acceso</h2>
            <p>
              Al conectar su cuenta de QuickBooks Online, usted autoriza
              expresamente a Citrus eCF a:
            </p>
            <ul>
              <li>
                Acceder a sus datos contables de QuickBooks Online (facturas, compras, notas
                de crédito, clientes, suplidores, artículos e impuestos) a través
                de la API oficial de Intuit con el alcance OAuth 2.0 de{" "}
                <strong>Accounting</strong> (lectura y escritura).
              </li>
              <li>
                Crear y actualizar campos personalizados en sus registros de QuickBooks Online
                para almacenar información fiscal del eCF.
              </li>
              <li>
                Adjuntar archivos PDF y XML a sus registros de QuickBooks Online como evidencia
                del documento electrónico.
              </li>
              <li>
                Leer continuamente los nuevos documentos en su nombre para
                procesarlos y cumplir con los requisitos de la DGII.
              </li>
            </ul>
            <p>
              Puede revocar esta autorización en cualquier momento desconectando
              ("Desvincular") la Integración desde la plataforma Citrus eCF.
            </p>
          </div>

          {/* Section 7 */}
          <div className="legal-section">
            <h2>7. Exactitud de los Datos y Responsabilidad del Usuario</h2>
            <p>Usted es el único responsable de:</p>
            <ul>
              <li>
                La exactitud e integridad de los datos ingresados en QuickBooks
                Online, incluyendo los números de RNC/identificación de clientes,
                configuraciones de impuestos e importes de transacciones.
              </li>
              <li>
                Asegurarse de que su uso de la Integración cumpla con todas las
                regulaciones de la DGII y la legislación tributaria dominicana
                aplicable.
              </li>
              <li>
                Revisar los documentos eCF generados y transmitidos a la DGII en
                su nombre.
              </li>
            </ul>
            <p>
              Citrus no se hace responsable de sanciones de la DGII, rechazos o
              incumplimientos fiscales derivados de datos incorrectos o
              incompletos en su cuenta de QuickBooks Online.
            </p>
          </div>

          {/* Section 8 */}
          <div className="legal-section">
            <h2>8. Propiedad Intelectual</h2>
            <p>
              Todos los derechos de propiedad intelectual sobre la Integración,
              incluidos el software, los algoritmos y la documentación, son y
              permanecen como propiedad exclusiva de Citrus Technology. Este
              Acuerdo no le otorga ningún derecho de propiedad.
            </p>
          </div>

          {/* Section 9 */}
          <div className="legal-section">
            <h2>9. Exclusión de Garantías</h2>
            <p className="legal-caps">
              LA INTEGRACIÓN SE PROPORCIONA "TAL COMO ESTÁ" Y "SEGÚN
              DISPONIBILIDAD", SIN GARANTÍAS DE NINGÚN TIPO, EXPRESAS O
              IMPLÍCITAS, INCLUYENDO, ENTRE OTRAS, LAS GARANTÍAS DE
              COMERCIABILIDAD, IDONEIDAD PARA UN PROPÓSITO PARTICULAR Y NO
              INFRACCIÓN. CITRUS NO GARANTIZA QUE LA INTEGRACIÓN SEA
              ININTERRUMPIDA, LIBRE DE ERRORES, NI QUE LOS DEFECTOS SERÁN
              CORREGIDOS.
            </p>
          </div>

          {/* Section 10 */}
          <div className="legal-section">
            <h2>10. Limitación de Responsabilidad</h2>
            <p className="legal-caps">
              EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY APLICABLE, CITRUS NO SERÁ
              RESPONSABLE POR DAÑOS INDIRECTOS, INCIDENTALES, ESPECIALES,
              CONSECUENTES O PUNITIVOS, INCLUIDOS LA PÉRDIDA DE GANANCIAS,
              PÉRDIDA DE DATOS O INTERRUPCIÓN DEL NEGOCIO, QUE SURJAN O ESTÉN
              RELACIONADOS CON SU USO DE LA INTEGRACIÓN, INCLUSO SI SE LE HA
              ADVERTIDO DE LA POSIBILIDAD DE DICHOS DAÑOS. LA RESPONSABILIDAD
              TOTAL DE CITRUS NO EXCEDERÁ LOS MONTOS PAGADOS POR USTED POR EL
              SERVICIO CITRUS eCF EN LOS TRES (3) MESES ANTERIORES AL RECLAMO.
            </p>
          </div>

          {/* Section 11 */}
          <div className="legal-section">
            <h2>11. Terminación</h2>
            <p>
              Este Acuerdo estará vigente hasta su terminación. Terminará
              automáticamente si usted desconecta su cuenta de QuickBooks,
              cancela su suscripción a Citrus eCF o viola cualquier término de
              este Acuerdo. Tras la terminación, su derecho a utilizar la
              Integración cesa de inmediato y Citrus eliminará sus tokens OAuth
              almacenados.
            </p>
          </div>

          {/* Section 12 */}
          <div className="legal-section">
            <h2>12. Modificaciones</h2>
            <p>
              Citrus se reserva el derecho de modificar este Acuerdo en cualquier
              momento. Le notificaremos los cambios materiales a través de la
              plataforma Citrus eCF o por correo electrónico. El uso continuado
              de la Integración después de la fecha de vigencia de los cambios
              constituye la aceptación del Acuerdo revisado.
            </p>
          </div>

          {/* Section 13 */}
          <div className="legal-section">
            <h2>13. Ley Aplicable</h2>
            <p>
              Este Acuerdo se regirá e interpretará de conformidad con las leyes
              de la <strong>República Dominicana</strong>. Cualquier disputa será
              resuelta ante los tribunales de Santo Domingo, República
              Dominicana.
            </p>
          </div>

          {/* Section 14 */}
          <div className="legal-section">
            <h2>14. Contacto</h2>
            <p>Para preguntas sobre este Acuerdo, contáctenos en:</p>
            <p>
              <strong>Citrus Technology</strong>
              <br />
              Correo electrónico:{" "}
              <a
                href="mailto:soporte@citrus.com.do"
                style={{ color: accentColor }}
              >
                soporte@citrus.com.do
              </a>
              <br />
              Sitio web:{" "}
              <a
                href="https://citrus.com.do"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: accentColor }}
              >
                citrus.com.do
              </a>
            </p>
          </div>

          {/* Footer */}
          <div className="legal-footer">
            <a href="/" className="legal-back-link">
              &#8592; Volver al inicio
            </a>
            <div className="legal-nav-links">
              <a href="/privacidad">Política de Privacidad</a>
              <a href="/quickbooks-integration">Integración QuickBooks</a>
            </div>
            <p className="legal-copyright">
              &copy; {new Date().getFullYear()} Citrus Technology. Todos los
              derechos reservados.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
